import { fetch } from '@/common/request';

// 智能体配置
export interface AgentConfigVo {
  id: number;
  agentCode: string;
  name: string;
  description?: string;
  avatarUrl?: string;
  modelConfigId?: number;
  promptTemplateId?: number;
  maxIters?: number;
  enableThinking?: number;
  enablePlan?: number;
  showInCard?: number;
  recordHistory?: number;
  recommendedQuestions?: string[];
  kbId?: number;
  ragMode?: string;
  ragTopK?: number;
  ragThreshold?: number;
  hookConfig?: string;
  status?: number;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AgentConfigCreateReq {
  agentCode: string;
  name: string;
  description?: string;
  avatarUrl?: string;
  modelConfigId?: number;
  promptTemplateId?: number;
  maxIters?: number;
  enableThinking?: number;
  enablePlan?: number;
  showInCard?: number;
  recordHistory?: number;
  recommendedQuestions?: string[];
  kbId?: number;
  ragMode?: string;
  ragTopK?: number;
  ragThreshold?: number;
  hookConfig?: string;
}

export interface AgentConfigUpdateReq extends AgentConfigCreateReq {
  id: number;
}

export interface AgentConfigCopyReq {
  sourceId: number;
  newAgentCode: string;
  newName?: string;
}

export interface AgentConfigListReq {
  keyword?: string;
  status?: number;
}

export interface AgentConfigToggleReq {
  id: number;
  status: number;
}

// Skill 绑定
export interface AgentSkillBindingVo {
  id: number;
  agentId: number;
  skillId: number;
  enabled?: number;
  createdAt?: string;
}

export interface AgentSkillBindingReq {
  agentId: number;
  skillId: number;
}

export interface AgentSkillBatchBindingReq {
  agentId: number;
  bindings: AgentSkillBindingReq[];
}

export interface SkillBindingToggleReq {
  agentId: number;
  skillId: number;
  enabled?: number;
}

export interface AgentSkillBindingListReq {
  agentId: number;
}

// 智能体配置 API
export function createAgentConfig(params: AgentConfigCreateReq): Promise<number> {
  return fetch(
    '/api/config/agent/create',
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export function updateAgentConfig(params: AgentConfigUpdateReq): Promise<void> {
  return fetch(
    '/api/config/agent/update',
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export function deleteAgentConfig(id: number): Promise<void> {
  return fetch(
    '/api/config/agent/delete',
    {
      method: 'POST',
      body: JSON.stringify({ id }),
    },
    true
  );
}

export function copyAgentConfig(params: AgentConfigCopyReq): Promise<number> {
  return fetch(
    '/api/config/agent/copy',
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export function toggleAgentConfig(params: AgentConfigToggleReq): Promise<void> {
  return fetch(
    '/api/config/agent/toggle',
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export function getAgentConfigDetail(id: number): Promise<AgentConfigVo> {
  return fetch(
    '/api/config/agent/detail',
    {
      method: 'POST',
      body: JSON.stringify({ id }),
    },
    true
  );
}

export function getAgentConfigList(params?: AgentConfigListReq): Promise<AgentConfigVo[]> {
  return fetch(
    '/api/config/agent/list',
    {
      method: 'POST',
      body: JSON.stringify(params || {}),
    },
    true
  );
}

export function buildAgent(id: number): Promise<string> {
  return fetch(
    '/api/config/agent/build',
    {
      method: 'POST',
      body: JSON.stringify({ id }),
    },
    true
  );
}

// Skill 绑定 API
export function bindSkill(params: AgentSkillBindingReq): Promise<number> {
  return fetch(
    '/api/config/agent/skill-binding/bind',
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export function batchBindSkills(params: AgentSkillBatchBindingReq): Promise<void> {
  return fetch(
    '/api/config/agent/skill-binding/batchBind',
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export function unbindSkill(agentId: number, skillId: number): Promise<void> {
  return fetch(
    '/api/config/agent/skill-binding/unbind',
    {
      method: 'POST',
      body: JSON.stringify({ agentId, skillId }),
    },
    true
  );
}

export function toggleSkillBinding(params: SkillBindingToggleReq): Promise<void> {
  return fetch(
    '/api/config/agent/skill-binding/toggle',
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export function getSkillBindings(agentId: number): Promise<AgentSkillBindingVo[]> {
  return fetch(
    '/api/config/agent/skill-binding/list',
    {
      method: 'POST',
      body: JSON.stringify({ agentId }),
    },
    true
  );
}

export function getEnabledSkillBindings(agentId: number): Promise<AgentSkillBindingVo[]> {
  return fetch(
    '/api/config/agent/skill-binding/enabled',
    {
      method: 'POST',
      body: JSON.stringify({ agentId }),
    },
    true
  );
}

export const RAG_MODE_OPTIONS = [
  { label: '自动检索', value: 'auto' },
  { label: '强制检索', value: 'force' },
  { label: '关闭检索', value: 'off' },
];

// AgentConfigInfo（智能体配置详情）
export interface AgentConfigInfo {
  id: number;
  agentCode: string;
  name: string;
  description?: string;
  model?: {
    configId: number;
    provider: string;
    modelName: string;
    temperature?: number;
    enableThinking?: boolean;
  };
  prompt?: {
    templateId: number;
    templateName: string;
    currentVersion?: string;
    sysPromptLength?: number;
  };
  skills?: Array<{
    skillId: number;
    skillName: string;
    description?: string;
    currentVersion?: string;
    tools?: string[];
  }>;
  knowledge?: {
    kbId: number;
    kbName: string;
    ragMode?: string;
    ragTopK?: number;
    ragThreshold?: number;
  };
  runtime?: {
    maxIters?: number;
    enablePlan?: boolean;
    enableThinking?: boolean;
    hooks?: string[];
  };
}

// 获取智能体配置信息（用于前端展示）
export function getAgentConfigInfo(id: number): Promise<AgentConfigInfo> {
  return fetch(
    '/api/config/agent/configInfo',
    {
      method: 'POST',
      body: JSON.stringify({ id }),
    },
    true
  );
}
